import {ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { switchMap } from 'rxjs/operators';
import { LocalStorageKeys  } from '../../models/server/Constants';
import { OrganisationService } from '../../services/organisation.service';
import { UserService } from '../../services/user.service';
import { AuthenticationService } from '../authentication.service';
import { WarningPanelComponent } from '../../comps/warning-panel/warning-panel.component';
import { SimpleProgressComponent } from '../../comps/simpleprogress.component';
import { BGAnimationComponent } from '../../comps/bganimation/bganimation.component';
import { AuthShellComponent } from '../auth-shell/auth-shell.component';
import { MaterialModule } from '../../material.module';
import {LocalStoreService} from "../../services/local-store.service";
import { environment } from '../../../environments/environment';

@Component({
    selector: 'app-sign-in',
    templateUrl: './sign-in.component.html',
    styleUrls: ['./sign-in.component.scss'],
  imports: [MaterialModule, BGAnimationComponent, AuthShellComponent, FormsModule, ReactiveFormsModule, SimpleProgressComponent, RouterLink, WarningPanelComponent]
})
export class SignInComponent implements OnInit {

  environment = environment;

  // injected services
  private localStore = inject(LocalStoreService);
  private fb = inject(UntypedFormBuilder);
  private router = inject(Router);
  private authService = inject(AuthenticationService);
  private orgService = inject(OrganisationService);
  private userService = inject(UserService);
  private ngxLogger = inject(NGXLogger);
  private cdr = inject(ChangeDetectorRef);

  form: UntypedFormGroup;
  error: string;
  busy: boolean;
  showConfirm = false;

  // Amplify persists the Cognito session in cookies in prod/test (see environment.aws.cookieStorage) -
  // if the browser has cookies disabled, sign-in can appear to succeed but the session never
  // persists. Check up front so we can block login and explain why instead (#785).
  cookiesEnabled = this.checkCookiesEnabled();

  constructor() {
    const route = inject(ActivatedRoute);

    this.createForm();

    route.queryParamMap.subscribe(
      params => {
        if (params.get('user')) {
          this.form.controls.email.setValue(params.get('user'));
          this.cdr.markForCheck();
        }
      });
  }

  ngOnInit(): void {
    this.isAlreadySignedIn = this.authService.isAuthenticated();
  }

  // Landing on /signin while already authenticated (e.g. browser back button, a stale tab,
  // a bookmark) used to fall through to login() attempting to sign in again, which Amplify
  // rejects and which surfaced as an unhelpful "unknown error" - offer a clear way out instead.
  // Captured once on load: a live check would flip to true the moment login() succeeds and
  // flash this panel while the post-login navigation is still in flight.
  isAlreadySignedIn = false;

  continueToApp(): void {
    this.router.navigateByUrl('/design');
  }

  signOut(): void {
    this.authService.logout();
  }

  confirm(): void {
    this.router.navigate(['/confirm'], { queryParams: { user: this.form.controls.email.value } });
  }

  async login() {

    this.error = '';
    this.busy = false;
    this.showConfirm = false;
    this.cdr.markForCheck();

    if (!this.cookiesEnabled) {
      this.error = 'Cookies are disabled in your browser. Please enable cookies and try again.';
      this.cdr.markForCheck();
      return;
    }

    if (this.form.valid) {
      this.cdr.markForCheck();
      this.busy = true;

      try {
        await this.authService.login(
          this.form.controls.email.value,
          this.form.controls.password.value).then(result => {

            this.localStore.removeDBItem(LocalStorageKeys.DesignManager_ReturnToLabel).then();

            if (result) {
              this.orgService.resetCache().pipe(
                switchMap(_ =>
                  this.userService.resetCache()
                ),
                switchMap(_ =>
                  // get the org to force creation of it before we get any further
                  this.orgService.getOrganisation()
                ),
                switchMap(_ =>
                  this.userService.getUser())
              ).subscribe(() => {
                this.localStore.removeDBItem(LocalStorageKeys.DesignManager_ReturnToLabel).then();
                this.cdr.markForCheck();
              });
            } else {
              this.error = 'You are not registered or your credentials are invalid.  Try again or register first.  If you have registered then check your email for a confirmation link.';
              this.cdr.markForCheck();
            }
          }).catch(err => {

            this.ngxLogger.error(err);
            this.busy = false;

            this.error = 'There was an unknown error.  Please try again later.';

            if (err.name === 'UserNotFoundException' || err.name === 'NotAuthorizedException') {
              this.error = 'You are not registered or your credentials are invalid.  Try again or register first.';
            } else if (err.name === 'UserNotConfirmedException') {
              this.error = 'You have not confirmed your login. Check your email for the confirmation code.';
              this.showConfirm = true;
            }

            this.cdr.markForCheck();
          });

      } finally {
        this.busy = false;
        this.cdr.markForCheck();
      }
    }

  }

  // navigator.cookieEnabled can lie in some browsers/configurations (eg. still reports true
  // with third-party or all-cookie blocking active), so confirm by actually writing and
  // reading back a test cookie rather than trusting the flag alone.
  private checkCookiesEnabled(): boolean {
    if (!navigator.cookieEnabled) {
      return false;
    }

    try {
      const testCookie = 'barxui_cookie_check=1';
      document.cookie = `${testCookie}; path=/; SameSite=Lax`;
      const cookiesWritable = document.cookie.indexOf(testCookie) !== -1;
      document.cookie = `${testCookie}; path=/; SameSite=Lax; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
      return cookiesWritable;
    } catch {
      return false;
    }
  }

  private createForm(): void {
    this.form = this.fb.group({
      email: new UntypedFormControl('', [Validators.required, Validators.email]),
      password: new UntypedFormControl('', Validators.required)
    })
  }
}
